import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/franchisePay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocUpload } from "./DocUpload";
import { toast } from "sonner";

const STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"];
const TYPES = ["Training institute", "Coaching centre", "College / University", "School", "Skill development centre", "Other"];
const REG_TYPES = ["Proprietorship", "Partnership", "LLP", "Private Limited", "Trust / Society", "Not registered"];

const validate = (f, docs) => {
  const e = {};
  ["institute_name", "institute_type", "address", "state", "district", "rep_name", "rep_designation", "facilities", "student_capacity"].forEach((k) => { if (!String(f[k] || "").trim()) e[k] = "Required"; });
  if (!/^\d{6}$/.test(f.pincode || "")) e.pincode = "Enter a 6-digit PIN code";
  if (!/^[6-9]\d{9}$/.test((f.mobile || "").replace(/\D/g, "").slice(-10))) e.mobile = "Enter a valid 10-digit mobile";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email || "")) e.email = "Enter a valid email";
  if (!docs.length) e.documents = "Upload at least one document";
  if (!f.accept_terms) e.accept_terms = "Please accept to continue";
  return e;
};

function F({ id, label, error, children }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-1">{children}</div>
      {error && <p className="text-xs text-crimson mt-1" data-testid={`fr-err-${id}`}>{error}</p>}
    </div>
  );
}

export function ApplicationForm({ user, onSubmitted }) {
  const [f, setF] = useState({ email: user.email, rep_name: user.name, mobile: user.mobile || "", accept_terms: false });
  const [docs, setDocs] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [started, setStarted] = useState(false);
  const set = (k, v) => {
    if (!started) { setStarted(true); track("application_start"); }
    setF((p) => ({ ...p, [k]: v }));
  };
  const inp = (k, props = {}) => <Input id={k} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} aria-invalid={!!errors[k]} data-testid={`fr-${k}`} {...props} />;
  const pick = (k, opts) => (
    <Select value={f[k]} onValueChange={(v) => set(k, v)}>
      <SelectTrigger id={k} data-testid={`fr-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f, docs);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please fix the highlighted fields"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/franchise/applications", { ...f, documents: docs });
      track("application_submit", { ref: data.ref_no });
      toast.success(`Application ${data.ref_no} submitted`);
      onSubmitted();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="space-y-8" noValidate data-testid="fr-application-form">
      <Block title="Institute details">
        <F id="institute_name" label="Institute name *" error={errors.institute_name}>{inp("institute_name")}</F>
        <F id="institute_type" label="Institute type *" error={errors.institute_type}>{pick("institute_type", TYPES)}</F>
        <div className="sm:col-span-2"><F id="address" label="Registered address *" error={errors.address}><Textarea id="address" rows={2} value={f.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="fr-address" /></F></div>
        <F id="state" label="State *" error={errors.state}>{pick("state", STATES)}</F>
        <F id="district" label="District *" error={errors.district}>{inp("district")}</F>
        <F id="pincode" label="PIN code *" error={errors.pincode}>{inp("pincode", { inputMode: "numeric", maxLength: 6 })}</F>
      </Block>
      <Block title="Authorized representative">
        <F id="rep_name" label="Full name *" error={errors.rep_name}>{inp("rep_name")}</F>
        <F id="rep_designation" label="Designation *" error={errors.rep_designation}>{inp("rep_designation", { placeholder: "e.g. Director" })}</F>
        <F id="mobile" label="Mobile number *" error={errors.mobile}>{inp("mobile", { inputMode: "tel" })}</F>
        <F id="email" label="Email *" error={errors.email}>{inp("email", { type: "email" })}</F>
      </Block>
      <Block title="Business registration (where applicable)">
        <F id="registration_type" label="Registration type">{pick("registration_type", REG_TYPES)}</F>
        <F id="registration_number" label="Registration / CIN number">{inp("registration_number")}</F>
        <F id="gstin" label="GSTIN">{inp("gstin", { placeholder: "Optional" })}</F>
      </Block>
      <Block title="Facilities & capacity">
        <div className="sm:col-span-2"><F id="facilities" label="Available facilities *" error={errors.facilities}><Textarea id="facilities" rows={3} placeholder="Classrooms, computer lab, internet, projector..." value={f.facilities || ""} onChange={(e) => set("facilities", e.target.value)} data-testid="fr-facilities" /></F></div>
        <F id="student_capacity" label="Student capacity (per batch) *" error={errors.student_capacity}>{inp("student_capacity", { type: "number", min: 1 })}</F>
      </Block>
      <div className="bg-white border border-border rounded-2xl p-6">
        <h3 className="font-heading font-bold text-navy mb-4">Supporting documents *</h3>
        <DocUpload docs={docs} setDocs={setDocs} />
        {errors.documents && <p className="text-xs text-crimson mt-2" data-testid="fr-err-documents">{errors.documents}</p>}
      </div>
      <label className="flex gap-3 items-start text-sm">
        <input type="checkbox" className="mt-1" checked={f.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)} data-testid="fr-accept_terms" />
        <span>I accept the <Link to="/page/franchise-agreement" target="_blank" className="underline">franchise terms</Link>, <Link to="/page/commission-policy" target="_blank" className="underline">commission policy</Link> and <Link to="/page/privacy-policy" target="_blank" className="underline">privacy policy</Link>.</span>
      </label>
      {errors.accept_terms && <p className="text-xs text-crimson -mt-6" data-testid="fr-err-accept_terms">{errors.accept_terms}</p>}
      <Button type="submit" disabled={saving} className="w-full sm:w-auto rounded-full viriks-amber text-white font-semibold px-8 h-12" data-testid="fr-submit">{saving ? "Submitting..." : "Submit application"}</Button>
    </form>
  );
}

function Block({ title, children }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <h3 className="font-heading font-bold text-navy mb-4">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
